import {
  Alert,
  Badge,
  Button,
  Card,
  IconButton,
  Spinner,
  Stack,
  Table,
  Text,
} from '@chakra-ui/react';
import { toaster } from '@mono/chakra';
import { FC, useMemo, useState } from 'react';
import { LuPencil, LuPlus, LuTrash, LuUsers } from 'react-icons/lu';
import ManageTeamModal from './ManageTeamModal';
import ManageTeamMembersModal from './ManageTeamMembersModal';
import DeleteTeamDialog from './components/DeleteTeamDialog';
import { useCurrentOrganization } from '../organizations/hooks';
import { TeamDetails, useTeamsSettings } from './hooks';

const TeamsSettingsTab: FC = () => {
  const currentOrganization = useCurrentOrganization();
  const {
    teams,
    isLoading,
    loadError,
    refresh,
    createTeam,
    updateTeam,
    deleteTeam,
    isMutating,
    mutateError,
  } = useTeamsSettings();
  const [modalState, setModalState] = useState<{
    mode: 'create' | 'edit';
    team?: TeamDetails;
  } | null>(null);
  const [teamPendingDeletion, setTeamPendingDeletion] =
    useState<TeamDetails | null>(null);
  const [teamManagingMembers, setTeamManagingMembers] =
    useState<TeamDetails | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  const handleCloseModal = () => setModalState(null);

  const handleSubmit = async (values: { name: string }) => {
    if (!modalState) return;

    if (modalState.mode === 'create') {
      await createTeam(values);
    } else if (modalState.mode === 'edit' && modalState.team) {
      await updateTeam(modalState.team.id, values);
    }
  };

  const handleDelete = async () => {
    if (!teamPendingDeletion) return;
    setDeleteError(null);
    try {
      await deleteTeam(teamPendingDeletion.id);
      toaster.create({
        type: 'success',
        title: 'Team deleted',
        description: `"${teamPendingDeletion.name}" has been removed.`,
      });
      setTeamPendingDeletion(null);
      if (teamManagingMembers?.id === teamPendingDeletion.id) {
        setTeamManagingMembers(null);
      }
    } catch (error) {
      const message =
        error instanceof Error ? error.message : 'Unable to delete team.';
      setDeleteError(message);
      toaster.create({
        type: 'error',
        title: 'Failed to delete team',
        description: message,
      });
    }
  };

  const tableContent = useMemo(() => {
    if (isLoading) {
      return (
        <Stack align="center" gap="3" py="6">
          <Spinner />
          <Text fontSize="sm" color="fg.muted">
            Loading teams…
          </Text>
        </Stack>
      );
    }

    if (loadError) {
      return (
        <Stack gap="4">
          <Alert.Root status="error">
            <Alert.Indicator />
            <Alert.Content>
              <Alert.Title>Unable to load teams</Alert.Title>
              <Alert.Description>{loadError}</Alert.Description>
            </Alert.Content>
          </Alert.Root>
          <Button onClick={() => void refresh()} alignSelf="flex-start">
            Try again
          </Button>
        </Stack>
      );
    }

    if (!currentOrganization) {
      return (
        <Alert.Root status="warning">
          <Alert.Indicator />
          <Alert.Content>
            <Alert.Title>No organization selected</Alert.Title>
            <Alert.Description>
              Choose an organization to manage its teams.
            </Alert.Description>
          </Alert.Content>
        </Alert.Root>
      );
    }

    if (teams.length === 0) {
      return (
        <Alert.Root status="info">
          <Alert.Indicator />
          <Alert.Content>
            <Alert.Title>No teams yet</Alert.Title>
            <Alert.Description>
              Create your first team to start organizing work.
            </Alert.Description>
          </Alert.Content>
        </Alert.Root>
      );
    }

    return (
      <Table.ScrollArea>
        <Table.Root>
          <Table.Header>
            <Table.Row>
              <Table.ColumnHeader>Name</Table.ColumnHeader>
              <Table.ColumnHeader textAlign="center">
                Members
              </Table.ColumnHeader>
              <Table.ColumnHeader textAlign="right" width="1%">
                Actions
              </Table.ColumnHeader>
            </Table.Row>
          </Table.Header>
          <Table.Body>
            {teams.map((team) => (
              <Table.Row key={team.id}>
                <Table.Cell>
                  <Text fontWeight="medium">{team.name}</Text>
                </Table.Cell>
                <Table.Cell textAlign="center">
                  <Badge variant="subtle">{team.membersCount}</Badge>
                </Table.Cell>
                <Table.Cell textAlign="right">
                  <Stack direction="row" justify="flex-end" gap="1">
                    <IconButton
                      aria-label={`Manage members for ${team.name}`}
                      size="sm"
                      variant="ghost"
                      onClick={() => setTeamManagingMembers(team)}
                    >
                      <LuUsers />
                    </IconButton>
                    <IconButton
                      aria-label={`Edit ${team.name}`}
                      size="sm"
                      variant="ghost"
                      onClick={() => setModalState({ mode: 'edit', team })}
                    >
                      <LuPencil />
                    </IconButton>
                    <IconButton
                      aria-label={`Delete ${team.name}`}
                      size="sm"
                      variant="ghost"
                      colorPalette="red"
                      onClick={() => {
                        setDeleteError(null);
                        setTeamPendingDeletion(team);
                      }}
                    >
                      <LuTrash />
                    </IconButton>
                  </Stack>
                </Table.Cell>
              </Table.Row>
            ))}
          </Table.Body>
        </Table.Root>
      </Table.ScrollArea>
    );
  }, [currentOrganization, isLoading, loadError, refresh, teams]);

  return (
    <Stack gap="6">
      <Card.Root>
        <Card.Header
          gap="3"
          display="flex"
          justifyContent="space-between"
          alignItems="flex-start"
        >
          <Stack gap="1">
            <Card.Title>Teams</Card.Title>
            <Card.Description>
              Manage teams in your organization.
            </Card.Description>
          </Stack>
          <Button
            size="sm"
            onClick={() => setModalState({ mode: 'create' })}
            disabled={!currentOrganization}
            display="inline-flex"
            alignItems="center"
            gap="2"
          >
            <LuPlus /> Add team
          </Button>
        </Card.Header>
        <Card.Body gap="4">
          {tableContent}
          {mutateError && !modalState && !teamPendingDeletion ? (
            <Text color="fg.error" fontSize="sm">
              {mutateError}
            </Text>
          ) : null}
        </Card.Body>
      </Card.Root>
      <ManageTeamModal
        isOpen={modalState !== null}
        mode={modalState?.mode ?? 'create'}
        defaultValues={
          modalState?.mode === 'edit' && modalState.team
            ? { id: modalState.team.id, name: modalState.team.name }
            : undefined
        }
        onClose={handleCloseModal}
        onSubmit={handleSubmit}
      />
      <DeleteTeamDialog
        isOpen={Boolean(teamPendingDeletion)}
        isLoading={isMutating}
        teamName={teamPendingDeletion?.name}
        error={deleteError}
        onCancel={() => {
          setDeleteError(null);
          setTeamPendingDeletion(null);
        }}
        onConfirm={handleDelete}
      />
      <ManageTeamMembersModal
        isOpen={teamManagingMembers !== null}
        team={teamManagingMembers}
        onClose={() => setTeamManagingMembers(null)}
        onMembersChanged={async () => {
          await refresh();
        }}
      />
    </Stack>
  );
};

export default TeamsSettingsTab;
